import { useEffect, useState } from "react";
import { useDevice } from "../../../common/hooks/useDevice";
import { SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomTextCard from "../../../common/components/card/CustomTextCard";
import styled from "styled-components";
import { useWindowSize } from "../../../common/hooks/useWindowSize";
import Translate from "../../../common/translate/Translate";
import RevenueService from "../../../service/RevenueService";
import { format, subDays } from "date-fns";

const TabletTextCardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 17px;
    height: 357px;
`;

const WebTextCardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 17px;
    height: 170px;
`;

function FranchiseManagerDashboardTextCardArea() {
    const { width } = useWindowSize();
    const currentDevice = useDevice();
    const revenueService = new RevenueService();

    const [revenueTotal, setRevenueTotal] = useState({
        totalRevenue: 0,
        dailyAverage: 0,
        monthly: 0,
    });

    const [deviceData, setDeviceData] = useState({
        total: 0,
        active: 0,
        notAvailable: 0,
        freeCycle: 0,
    });

    const [totalCycle, setTotalCycle] = useState(0);

    const fetchData = () => {
        const endDate = format(new Date(), "yyyy-MM-dd");
        const startDate = format(subDays(new Date(), 7), "yyyy-MM-dd");

        revenueService
            .getRevenueTotal({ startDate, endDate })
            .then((result) => {
                // spec 04.01.05.05: [{ day, revenueTotal }] — 기간 전체 합계
                const list = Array.isArray(result) ? result : [];
                const sum = list.reduce((acc, item) => acc + (Number(item?.revenueTotal ?? item?.total) || 0), 0);
                setRevenueTotal({
                    totalRevenue: sum,
                    dailyAverage: list.length > 0 ? Math.round(sum / list.length) : 0,
                    monthly: sum,
                });
            })
            .catch((error) => {
                console.error("Error fetching revenue total:", error);
            });

        revenueService
            .getUsageDevice({ startDate, endDate })
            .then((result) => {
                // 응답 키는 totalDevice/activeDevice/notAvailableDevice/freeCycle 이며 각각 {total,washer,dryer,combo}
                setDeviceData({
                    total: result?.totalDevice?.total || 0,
                    active: result?.activeDevice?.total || 0,
                    notAvailable: result?.notAvailableDevice?.total || 0,
                    freeCycle: result?.freeCycle?.total || 0,
                });
            })
            .catch((error) => {
                console.error("Error fetching usage device:", error);
            });

        revenueService
            .getRevenueAnalysis({ startDate, endDate })
            .then((result) => {
                setTotalCycle(result.totalCycle ?? 0);
            })
            .catch((error) => {
                console.error("Error fetching revenue analysis:", error);
            });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const totalSalesCard = (
        <CustomTextCard
            icon={"cashes"}
            title={Translate("dashboard.totalSales")}
            mainContent={`฿${(revenueTotal.totalRevenue ?? 0).toLocaleString()}`}
            percentage={10.5}
            leftBottomText={Translate("common.fromYesterday")}
            isColorPrimary={true}
            hasIcon={true}
        />
    );

    const totalDailyTransactionsCard = (
        <CustomTextCard
            icon={"receipt"}
            title={Translate("dashboard.totalDailyTransactions")}
            mainContent={String(totalCycle)}
            percentage={10.5}
            leftBottomText={Translate("common.fromYesterday")}
            hasIcon={true}
        />
    );

    const currentlyWorkingCard = (
        <CustomTextCard
            icon={"refresh"}
            title={Translate("dashboard.currentlyWorking")}
            mainContent={String(deviceData.active)}
            remainCount={String(deviceData.total)}
            hasIcon={true}
        />
    );

    const vacantCard = (
        <CustomTextCard
            icon={"red_loader"}
            title={Translate("dashboard.vacant")}
            mainContent={String(deviceData.freeCycle)}
            remainCount={String(deviceData.total)}
            hasIcon={true}
        />
    );

    if (currentDevice === "mobile") {
        return (
            <>
                <SizedBox height={"20px"} />
                {totalSalesCard}
                <SizedBox height={"16px"} />
                {totalDailyTransactionsCard}
                <SizedBox height={"16px"} />
                {currentlyWorkingCard}
                <SizedBox height={"16px"} />
                {vacantCard}
            </>
        );
    } else if (width <= 1400) {
        return (
            <>
                <SizedBox height={"24px"} />
                <TabletTextCardGrid>
                    {totalSalesCard}
                    {totalDailyTransactionsCard}
                    {currentlyWorkingCard}
                    {vacantCard}
                </TabletTextCardGrid>
            </>
        );
    } else {
        return (
            <>
                <SizedBox height={"24px"} />
                <WebTextCardGrid>
                    {totalSalesCard}
                    {totalDailyTransactionsCard}
                    {currentlyWorkingCard}
                    {vacantCard}
                </WebTextCardGrid>
            </>
        );
    }
}

export default FranchiseManagerDashboardTextCardArea;
